import type { LockedFeatureKey } from '../../constants/bloqueios';

// ─── OS MÓDULOS DO PERFIL, NA ORDEM DO MENU ─────────────────────────────────────────────────
//
// Uma lista só para a web (o SideNav, `src/ui-carbon/casca/modulos.ts`, que põe os ícones do
// `@carbon/icons-react`) e para o app nativo (o Menu, que põe os do `@carbon/icons`). A `chave` é o
// pedaço do endereço depois do perfil: `/artists/:id/<chave>` na web e `/artista/:id/<chave>` no
// app, para um link universal da web abrir a mesma tela no app.
//
// O Financeiro ainda não existe e não entra no menu: a rota dele diz isso na própria página. O
// Marketing entrou em 05/10/2026, depois de Músicas; desde 03/11/2026 vem antes dela (pedido do Lucas).
//
// O DESENVOLVIMENTO é um grupo: o Diagnóstico REAL, o Planejamento estratégico e o Plano de ação,
// cada um com a página dele. O CONTROLE DE SHOWS também: a venda (/vendas) e a produção
// (/producao). Os endereços antigos (/crm e /shows) redirecionam (Lucas, 27/09/2026). O MARKETING
// também, desde 01/11/2026 (pedido do Lucas, como o Controle de shows): o Branding, o Material de
// apresentação, o Plano de redes e a Gestão das Redes (o calendário, as ideias e o fechamento, em
// abas). A página /marketing (o Início, com a situação de cada parte) continua, pela trilha. No app
// nativo o Marketing é grupo desde 01/11/2026, com o Branding e o Material, como na web.

export interface PaginaDoGrupo {
  chave: string;
  rotulo: string;
  /** O nome inteiro, onde cabe (o menu do celular); no SideNav vale o `rotulo`. */
  nome?: string;
  /** O bloqueio que a rota pede (`RequireModuloLiberado`/`RequireArtistActive`). */
  trava?: LockedFeatureKey;
}

export interface ModuloDoMenu extends PaginaDoGrupo {
  /** Um grupo: o módulo abre estas páginas, em vez de ser uma página. */
  filhos?: PaginaDoGrupo[];
  /** No app nativo o grupo ainda é uma página só (a da `chave`), sem as filhas. */
  umaPaginaNoApp?: boolean;
}

export const MODULOS_DO_MENU: ModuloDoMenu[] = [
  { chave: '', rotulo: 'Início' },
  // A página pública do artista (o "link na bio" com o "Contrate meu show"), logo depois do Início
  // (pedido do Lucas, 03/10/2026).
  { chave: 'perfil-publico', rotulo: 'Perfil', trava: 'perfil' },
  {
    chave: 'desenvolvimento',
    rotulo: 'Desenvolvimento',
    filhos: [
      { chave: 'desenvolvimento/diagnostico', rotulo: 'Diagnóstico REAL', trava: 'plano' },
      { chave: 'desenvolvimento/planejamento', rotulo: 'Planejamento', nome: 'Planejamento estratégico', trava: 'plano' },
      { chave: 'desenvolvimento/plano', rotulo: 'Plano de ação', trava: 'plano' },
    ],
  },
  {
    chave: 'controle-de-shows',
    rotulo: 'Controle de shows',
    filhos: [
      { chave: 'vendas', rotulo: 'Vendas', trava: 'negociacoes' },
      { chave: 'producao', rotulo: 'Produção', trava: 'shows' },
      // O papel do show para a equipe, só para ler (03/11/2026): o músico vê sem acesso à Produção.
      { chave: 'cronograma', rotulo: 'Cronograma', trava: 'cronograma' },
    ],
  },
  {
    chave: 'marketing',
    rotulo: 'Marketing',
    trava: 'marketing',
    filhos: [
      { chave: 'marketing/branding', rotulo: 'Branding', trava: 'marketing' },
      { chave: 'marketing/material', rotulo: 'Material de apresentação', trava: 'marketing' },
      // Plano de redes e Gestão das Redes saíram em 01/11/2026 (reunião com a Anita): voltam depois
      // de refeitas. Até lá, só o admin as vê (`modulosDoMenu`); para os outros, os endereços levam
      // ao Branding.
    ],
  },
  // Músicas depois do Marketing (Lucas, 03/11/2026).
  { chave: 'catalog', rotulo: 'Músicas', trava: 'catalog' },
  // Os Lançamentos (03/11/2026): logo abaixo das Músicas, com acesso próprio na Equipe.
  { chave: 'lancamentos', rotulo: 'Lançamentos', trava: 'catalog' },
  { chave: 'agenda', rotulo: 'Agenda', trava: 'agenda' },
  { chave: 'team', rotulo: 'Equipe', trava: 'team' },
  { chave: 'arquivos', rotulo: 'Arquivos', trava: 'arquivos' },
  // A NYTA NO MENU (pedido da Anita, 09/10/2026): daqui ela abre em tela cheia, com a lista de
  // conversas.
  { chave: 'nyta', rotulo: 'Nyta IA', trava: 'nyta' },
];

/**
 * As páginas que só o admin da plataforma vê, no fim do grupo delas (Lucas, 02/11/2026: as redes
 * ocultas continuam abertas para o admin acompanhar a reformulação).
 */
const SO_DO_ADMIN: Record<string, PaginaDoGrupo[]> = {
  marketing: [
    { chave: 'marketing/redes/plano', rotulo: 'Plano de redes', trava: 'marketing' },
    { chave: 'marketing/redes', rotulo: 'Gestão das Redes', trava: 'marketing' },
  ],
};

/** O menu de quem está logado: o de todos, e o do admin com as páginas que só ele vê. */
export const modulosDoMenu = (admin: boolean): ModuloDoMenu[] => (admin
  ? MODULOS_DO_MENU.map((m) => (SO_DO_ADMIN[m.chave] ? { ...m, filhos: [...(m.filhos ?? []), ...SO_DO_ADMIN[m.chave]] } : m))
  : MODULOS_DO_MENU);

/** As páginas do menu, com os grupos abertos (a ordem de leitura). */
export const paginasDoMenu = (modulos: ModuloDoMenu[] = MODULOS_DO_MENU): PaginaDoGrupo[] =>
  modulos.flatMap((m) => (m.filhos ? m.filhos : [m]));

/** A página do menu em que a rota está: o pedaço depois do perfil começa pela chave dela. */
export const paginaDaRota = (resto: string, modulos: ModuloDoMenu[] = MODULOS_DO_MENU): PaginaDoGrupo | undefined => {
  const limpo = resto.replace(/^\/+|\/+$/g, '');
  if (!limpo) return paginasDoMenu(modulos).find((p) => p.chave === '');
  return paginasDoMenu(modulos)
    .filter((p) => p.chave)
    .sort((a, b) => b.chave.length - a.chave.length)
    .find((p) => limpo === p.chave || limpo.startsWith(`${p.chave}/`));
};
